import SystemToolbarView from "./systemToolbarView";
import {createSelector} from "@reduxjs/toolkit";
import {Presenter} from "../../../framework.visual/extras/presenter";
import {createComponentWrapper} from "../../../framework/wrappers/componentWrapper";
import {DocumentPanelId} from "../documentPanel/documentPanelPresenter";
import {LinksPanelId} from "../linksPanel/linksPanelPresenter";
import {DocPreviewSVG} from "../../theme/svgs/docPreviewSVG";
import {forEach} from "../../../framework.visual/extras/utils/collectionUtils";
import {
    authenticationService,
    authorizationService,
    displayService,
    repoService,
    selectionService
} from "../../../app.core/serviceComposition";
import {SystemToolMenuItem} from "../../model/systemToolMenuItem";
import {SystemToolVM} from "./systemToolbarModel";
import {PERMISSION_ENTITY, PERMISSION_LEVEL, PERMISSION_OPERATOR} from "../../../app.core.api";
import { UploadPanelId } from "../systemToolPanels/uploadPanel/uploadPanelPresenter";
import { KMApi } from "../../../km.api";

export const SYSTEM_TOOLBAR_VIEW_ID = 'system-tool-panel';

class SystemToolbar extends Presenter {
    constructor() {
        super();

        this.id ='components/systemToolbar';

        this.view = SystemToolbarView;

        this.mapStateToProps = (state: any, props: any) => {
            return {
                documentPreviewTool: this.getDocumentPreviewToolVM(state),
                linksTool: this.getLinksToolVM(state),
                tools: this.getToolVMs(state)
            }
        }

        this.mapDispatchToProps = () => {
            return {
                onDocumentPreviewSelected: () => this.onDocumentPreviewSelected(),
                onTestLambdaSelected: () => this.onTestLambdaSelected(),
                onToolSelected: (id: string) => this.onToolSelected(id)
            };
        }
    }

    getTools = () => {
        return repoService.getAll<SystemToolMenuItem>(SystemToolMenuItem.class);
    }

    onToolSelected(nextId: string) {
        let currentId = displayService.getSelectedNodeId(SYSTEM_TOOLBAR_VIEW_ID)

        if (currentId === nextId) {
            displayService.popNode(SYSTEM_TOOLBAR_VIEW_ID);
        }
        else {
            displayService.pushNode(nextId);
        }
    }


    getSelectedNode = () => {
        return displayService.getSelectedNodeId(SYSTEM_TOOLBAR_VIEW_ID);
    }

    getDocumentNodeInfo = () => {
        return displayService.getNodeInfo(DocumentPanelId);
    }

    getLinksNodeInfo = () => {
        return displayService.getNodeInfo(LinksPanelId);
    }

    getToolVMs = createSelector(
        [ this.getSelectedNode, this.getTools, authorizationService.getPermissions], // if this changes, will re-evaluate the combiner and trigger a re-render
        (selectedId, items) => {
            let itemVMs: Record<string, SystemToolVM> = {};

            forEach(items, (item: SystemToolMenuItem) => {

                let enabled = true;
                switch(item.id) {
                    case UploadPanelId:
                        enabled = authorizationService.hasPermission(PERMISSION_ENTITY.DOCUMENT, PERMISSION_OPERATOR.POST);
                        break;
                }

                if (enabled) {
                    itemVMs[item.id] = {
                        ...item,
                        selected: selectedId === item.id
                    };
                }
            });

            return Object.values(itemVMs);
        }
    );

    onDocumentPreviewSelected() {
        displayService.toggleNode(DocumentPanelId);

        if (selectionService.getContext("selected-document") !== '') {
            selectionService.setContext("selected-document", '');
        }
    }

    async onTestLambdaSelected() {
        console.log("Testing KM API");

        //Example accessing API with an auto-generated client
        //Note API token expires after 30 minutes or so and calls will start to fail. Seems like the
        //authenticationService should renew them but it doesn't. KM has the same problem though.
        const api = new KMApi({ BASE: process.env.REACT_APP_SERVER_URL, TOKEN: authenticationService.getToken() });
        const result = await api.users.search("Parker");
        
        //The auto-generated client can't tell when a result returns one object or many. So if we expect many
        //cast it to an array.
        const resultArray = <Array<any>>result;
        alert(resultArray[0].first_name); 

        //We could get the API token like this, but I think its better to use the stuff Tektonux already built for now.
        //const authapi = new KMApi({ BASE: 'https://launch.navyanalytics.com/KMAPI/' });
        //const apikey = await authapi.authorize.login({ username: 'joe.parker@teledyne.com', password: 'password' })
        //const api = new KMApi({ BASE: 'https://launch.navyanalytics.com/KMAPI/', TOKEN: apikey.toString() });|

        //If we didn't generate the client with "--name KMApi" this is how it would work
        //I decided to use the --name option because it required less imports, but not sure it is really better.
        //OpenAPI.TOKEN = await AuthorizeService.login({ username: 'joe.parker@teledyne.com', password: 'password' });
        //const res = await UsersService.search("Parker");

        //Commented below is Joseph B's original Lambda test
        //const api = 'https://ztdjfv0op0.execute-api.us-gov-east-1.amazonaws.com/staging';
        //const data = '{ "name" : "launchpad TESTER" }';

        //axios
        //    .post(api, data)
        //    .then((response) => {
        //        alert(response.data.body);
        //    })
        //    .catch((error) => {
        //        alert("ERROR:" + error);
        //    })
    }

    getDocumentPreviewToolVM = createSelector(
        [this.getDocumentNodeInfo],
        (nodeInfo) => {
            const itemVM = {
                id: DocumentPanelId,
                graphic: DocPreviewSVG,
                selected: nodeInfo ? nodeInfo.visible : false,
                title: 'Preview'
            }
            return itemVM;
        }
    );

    getLinksToolVM = createSelector(
        [this.getLinksNodeInfo],
        (nodeInfo) => {
            const itemVM = {
                id: LinksPanelId,
                graphic: DocPreviewSVG,
                selected: nodeInfo ? nodeInfo.visible : false,
                title: 'Links'
            }
            return itemVM;
        }
    );
}

export const {
    connectedPresenter: SystemToolbarPresenter,
    componentId: SystemToolbarId
} = createComponentWrapper(SystemToolbar);
